// 開發環境使用 proxy，生產環境根據 hostname 動態決定
const getApiBaseUrl = () => {
    // 1. 開發環境：使用 Vite proxy
    if (import.meta.env.DEV) {
        return '/api';
    }

    const hostname = window.location.hostname;

    // 2. 本機預覽/測試環境 (localhost, 127.0.0.1, 192.*)：使用環境變數或預設值
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.')) {
        return import.meta.env.VITE_API_URL || '/api';
    }

    // 3. 生產環境 (k8s internal, teegle.com.tw 等)：使用相對路徑
    // 這樣可以確保請求發送到與前端相同的 domain，避免跨域問題，並支援動態 subdomain
    return '/api';
};

export interface ApiError {
    error: string | { code?: string; message: string; details?: unknown };
    requestId?: string;
}

/** 非 2xx 回應。message 與一般 Error 相同；status 與 body 讓呼叫端辨識結構化錯誤（例如 SLOT_TAKEN） */
export class ApiRequestError extends Error {
    constructor(
        message: string,
        public readonly status: number,
        public readonly body: ApiError
    ) {
        super(message);
        this.name = 'ApiRequestError';
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

class ApiClient {
    private baseUrl: string;
    private refreshPromise: Promise<void> | null = null;
    private backendVersion: string | null = null;
    private backendBuildTime: string | null = null;

    constructor(baseUrl: string) {
        this.baseUrl = baseUrl;
    }

    getBackendVersion(): string | null {
        return this.backendVersion;
    }

    getBackendBuildTime(): string | null {
        return this.backendBuildTime;
    }

    private getAccessToken(): string | null {
        return localStorage.getItem('accessToken');
    }

    private getTenantId(): string | null {
        return localStorage.getItem('tenantId');
    }

    private async refreshToken(): Promise<void> {
        // 避免同時多個請求都去刷新 token
        if (this.refreshPromise) {
            return this.refreshPromise;
        }

        this.refreshPromise = (async () => {
            const refreshToken = localStorage.getItem('refreshToken');
            if (!refreshToken) {
                throw new Error('No refresh token');
            }

            try {
                const response = await fetch(`${this.baseUrl}/auth/refresh`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ refreshToken }),
                });

                if (!response.ok) {
                    throw new Error('Token refresh failed');
                }

                const data = await response.json();
                localStorage.setItem('accessToken', data.accessToken);
                localStorage.setItem('refreshToken', data.refreshToken);
                localStorage.setItem('user', JSON.stringify(data.user));
            } finally {
                this.refreshPromise = null;
            }
        })();

        return this.refreshPromise;
    }

    private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
        const url = `${this.baseUrl}${endpoint}`;

        // 準備 headers
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
        };

        // 合併 options headers
        if (options?.headers) {
            const optHeaders = new Headers(options.headers);
            optHeaders.forEach((value, key) => {
                headers[key] = value;
            });
        }

        // 加入 Access Token
        const accessToken = this.getAccessToken();
        if (accessToken) {
            headers['Authorization'] = `Bearer ${accessToken}`;
        }

        // 加入 Tenant ID
        const tenantId = this.getTenantId();
        if (tenantId) {
            headers['X-Tenant-ID'] = tenantId;
        }

        // 加入前端版本（預留給未來版本檢查用）
        headers['X-Frontend-Version'] = __APP_VERSION__;

        const config: RequestInit = {
            ...options,
            headers,
        };

        let response = await fetch(url, config);

        // 從 response header 取得後端版本資訊
        const backendVersion = response.headers.get('X-Backend-Version');
        const backendBuildTime = response.headers.get('X-Backend-Build-Time');
        if (backendVersion) {
            this.backendVersion = backendVersion;
        }
        if (backendBuildTime) {
            this.backendBuildTime = backendBuildTime;
        }

        // Token 過期，嘗試刷新
        if (response.status === 401 && accessToken) {
            try {
                await this.refreshToken();

                // 重試原請求
                const newAccessToken = this.getAccessToken();
                if (newAccessToken) {
                    headers['Authorization'] = `Bearer ${newAccessToken}`;
                }

                response = await fetch(url, {
                    ...options,
                    headers,
                });
            } catch (err) {
                // Refresh 失敗，清除 session 並跳轉到登入頁
                localStorage.clear();
                window.location.href = '/login';
                throw new Error('Session expired');
            }
        }

        if (!response.ok) {
            const data: ApiError = await response.json().catch(() => ({
                error: 'An unknown error occurred',
            }));

            let errorMessage = 'An unknown error occurred';
            if (typeof data.error === 'string') {
                errorMessage = data.error;
            } else if (data.error && typeof data.error === 'object' && data.error.message) {
                errorMessage = data.error.message;
            }

            throw new ApiRequestError(errorMessage, response.status, data);
        }

        if (response.status === 204 || response.status === 205) {
            return undefined as T;
        }

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
            return undefined as T;
        }

        return response.json();
    }

    async get<T>(endpoint: string): Promise<T> {
        return this.request<T>(endpoint, { method: 'GET' });
    }

    async post<T>(endpoint: string, data?: unknown): Promise<T> {
        return this.request<T>(endpoint, {
            method: 'POST',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    async put<T>(endpoint: string, data?: unknown): Promise<T> {
        return this.request<T>(endpoint, {
            method: 'PUT',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    async patch<T>(endpoint: string, data?: unknown): Promise<T> {
        return this.request<T>(endpoint, {
            method: 'PATCH',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    async delete<T>(endpoint: string): Promise<T> {
        return this.request<T>(endpoint, { method: 'DELETE' });
    }

    /**
     * 上傳檔案（FormData）
     * 不設定 Content-Type，讓 browser 自動設定 multipart/form-data boundary
     */
    async upload<T>(endpoint: string, formData: FormData): Promise<T> {
        const url = `${this.baseUrl}${endpoint}`;

        const headers: Record<string, string> = {};

        // 加入 Access Token
        const accessToken = this.getAccessToken();
        if (accessToken) {
            headers['Authorization'] = `Bearer ${accessToken}`;
        }

        // 加入 Tenant ID
        const tenantId = this.getTenantId();
        if (tenantId) {
            headers['X-Tenant-ID'] = tenantId;
        }

        // 加入前端版本
        headers['X-Frontend-Version'] = __APP_VERSION__;

        let response = await fetch(url, {
            method: 'POST',
            headers,
            body: formData,
        });

        // Token 過期，嘗試刷新
        if (response.status === 401 && accessToken) {
            try {
                await this.refreshToken();

                const newAccessToken = this.getAccessToken();
                if (newAccessToken) {
                    headers['Authorization'] = `Bearer ${newAccessToken}`;
                }

                response = await fetch(url, {
                    method: 'POST',
                    headers,
                    body: formData,
                });
            } catch {
                localStorage.clear();
                window.location.href = '/login';
                throw new Error('Session expired');
            }
        }

        if (!response.ok) {
            const data: ApiError = await response.json().catch(() => ({
                error: 'An unknown error occurred',
            }));

            let errorMessage = 'An unknown error occurred';
            if (typeof data.error === 'string') {
                errorMessage = data.error;
            } else if (data.error && typeof data.error === 'object' && data.error.message) {
                errorMessage = data.error.message;
            }

            throw new Error(errorMessage);
        }

        return response.json();
    }
}

export const apiClient = new ApiClient(getApiBaseUrl());
